import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Switch,
  Textarea,
  Typography,
} from '@healthinal/ui';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { getAppointmentDate, getAppointmentTimeFrom } from '../../../api/created/appointment.ts';
import {
  getGetLabOrdersByPatientQueryKey,
  useCreateXrayTest,
  useUpdateXrayTest,
} from '../../../api/generated/lab-order-controller/lab-order-controller.ts';
import {
  AddressContactDto,
  AddressDto,
  AppointmentDto,
  CreateXrayTestDto,
  CreateXrayTestDtoFinding,
  CreateXrayTestDtoFollowup,
  LabOrderDtoType,
} from '../../../api/generated/model';
import { queryClient } from '../../../api/queryClient.ts';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { getDefaultValueForDateInput, getDefaultValueForLocalTimeInput } from '../../../utils/date.ts';
import { blobToBase64 } from '../../../utils/file.ts';
import { getXrayFollowupName, getXrayInterpretationName, toLabOrderSamplingContact } from '../../../utils/labOrder.ts';
import { getStringOrUndefined } from '../../../utils/string.ts';
import { DeleteLabOrderButton } from './DeleteLabOrderButton.tsx';
import { LabOrderDetailsForm } from './LabOrderDetailsForm.tsx';

interface FormValues {
  samplingDate: string;
  samplingTime: string;
  samplingAddress: AddressDto | null;
  samplingContact: AddressContactDto | null;
  rxNumber: string;
  finding: CreateXrayTestDtoFinding;
  findingComment: string;
  findingDoctor: string;
  followup: CreateXrayTestDtoFollowup | null;
  fileLink: string;
  file: File | null;
  notes: string;
  isTodo: boolean;
}
export function CreateXrayResultForm({
  appointment,
  patientId,
  onClose,
  currentResult,
  currentResultId,
}: {
  appointment?: AppointmentDto | null;
  patientId: string;
  onClose: () => void;
  currentResult?: CreateXrayTestDto;
  currentResultId?: string;
}) {
  const isEditing = !!currentResultId;

  const formMethods = useForm<FormValues>({
    defaultValues: getDefaultValues(appointment, currentResult),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty: isFormDirty },
  } = formMethods;

  const isSamplingAddressDisabled = !!currentResult?.samplingAddress;
  const isSamplingContactDisabled = !!currentResult?.samplingContact;
  const isFileInputDisabled = !!currentResult?.file;
  const isRxNumberRequired = !!currentResult?.rxNumber;
  const isFindingRequired = !!currentResult?.finding;
  const isFindingCommentRequired = !!currentResult?.findingComment;
  const isFindingDoctorRequired = !!currentResult?.findingDoctor;
  const isFollowupRequired = !!currentResult?.followup;
  const isFileLinkRequired = !!currentResult?.fileLink;

  const createXrayTestMutation = useCreateXrayTest({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetLabOrdersByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim speichern des Testresultates ist ein Fehler aufgetreten'),
    },
  });

  const updateXrayTestMutation = useUpdateXrayTest({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetLabOrdersByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim Aktualisieren des Testresultats ist ein Fehler aufgetreten'),
    },
  });

  const handleOnSubmit = async (data: FormValues) => {
    const createXrayTestDto: CreateXrayTestDto = {
      patientId: patientId,
      samplingDate: data.samplingDate,
      samplingTime: getStringOrUndefined(data.samplingTime),
      appointmentId: appointment?.id,
      samplingAddress: data.samplingAddress ?? undefined,
      samplingContact: toLabOrderSamplingContact(data.samplingContact),
      rxNumber: getStringOrUndefined(data.rxNumber),
      finding: data.finding,
      findingComment: getStringOrUndefined(data.findingComment),
      findingDoctor: getStringOrUndefined(data.findingDoctor),
      followup: data.followup,
      file: data.file
        ? {
            fileName: data.file.name,
            file: await blobToBase64(data.file),
          }
        : undefined,
      fileLink: getStringOrUndefined(data.fileLink),
      notes: getStringOrUndefined(data.notes),
      isTodo: data.isTodo,
    };

    if (isEditing) {
      updateXrayTestMutation.mutate({ id: currentResultId, data: createXrayTestDto });
    } else {
      createXrayTestMutation.mutate({ data: createXrayTestDto });
    }
  };

  const handleOnClose = () => {
    reset();
    onClose();
  };
  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <Stack gap={2}>
          <Typography level={'title-lg'}>Röntgen-Thorax</Typography>
          <LabOrderDetailsForm
            samplingDate={'samplingDate'}
            samplingTime={'samplingTime'}
            samplingAddress={'samplingAddress'}
            samplingContact={'samplingContact'}
            file={'file'}
            isSamplingAddressDisabled={isSamplingAddressDisabled}
            isSamplingContactDisabled={isSamplingContactDisabled}
            fileInputName={'Röntgenbild'}
            fileInputPlaceHolder={currentResult?.file?.fileName ?? 'Röntgen'}
            fileInputMimeType={'image/jpeg'}
            isFileInputDisabled={isFileInputDisabled}
            isEditing={isEditing}
          />
          <Divider inset={'context'} />
          <Typography level={'title-lg'}>Resultat</Typography>
          <Stack gap={2} direction={'row'}>
            <FormControl sx={{ flex: 1 }} error={!!errors.rxNumber}>
              <FormLabel>
                <Typography>Röntgennummer {isRxNumberRequired && '*'}</Typography>
              </FormLabel>
              <Input {...register('rxNumber', { required: isRxNumberRequired })} />
              {errors.rxNumber && <FormHelperText>RX-Nummer muss angegeben werden</FormHelperText>}
            </FormControl>
            <FormControl sx={{ flex: 1 }} error={!!errors.findingDoctor}>
              <FormLabel>
                <Typography>Arzt {isFindingDoctorRequired && '*'}</Typography>
              </FormLabel>
              <Input {...register('findingDoctor', { required: isFindingDoctorRequired })} />
              {errors.findingDoctor && <FormHelperText>Arzt muss ausgefüllt werden</FormHelperText>}
            </FormControl>
          </Stack>
          <Stack gap={2} direction={'row'}>
            <FormControl sx={{ flex: 1 }} error={!!errors.finding}>
              <FormLabel>
                <Typography>Röntgenbefund {isFindingRequired && '*'}</Typography>
              </FormLabel>
              <Controller
                name={'finding'}
                control={control}
                rules={{ required: isFindingRequired }}
                render={({ field: { onChange, value } }) => (
                  <Select value={value} onChange={(_, value) => onChange(value)}>
                    {Object.values(CreateXrayTestDtoFinding).map((interpretation) => (
                      <Option value={interpretation} key={interpretation}>
                        {getXrayInterpretationName(interpretation)}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.finding && <FormHelperText>Röntgen Befund muss angegeben werden</FormHelperText>}
            </FormControl>

            <FormControl sx={{ flex: 1 }} error={!!errors.findingComment}>
              <FormLabel>
                <Typography>Kommentar Röntgenbefund {isFindingCommentRequired && '*'}</Typography>
              </FormLabel>
              <Textarea
                minRows={1}
                maxRows={4}
                {...register('findingComment', { required: isFindingCommentRequired })}
              />
              {errors.findingComment && <FormHelperText>Kommentar Röntgenbefund muss ausgefüllt werden</FormHelperText>}
            </FormControl>
          </Stack>
          <Stack gap={2} direction={'row'}>
            <FormControl sx={{ flex: 1 }} error={!!errors.followup}>
              <FormLabel>
                <Typography>Folgeprozedere {isFollowupRequired && '*'}</Typography>
              </FormLabel>
              <Controller
                name={'followup'}
                control={control}
                rules={{ required: isFollowupRequired }}
                render={({ field: { onChange, value } }) => (
                  <Select value={value} onChange={(_, value) => onChange(value)}>
                    {Object.values(CreateXrayTestDtoFollowup).map((followup) => (
                      <Option value={followup} key={followup}>
                        {getXrayFollowupName(followup)}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.followup && <FormHelperText>Folgeprozedere muss angegeben werden</FormHelperText>}
            </FormControl>
            <FormControl sx={{ flex: 1 }} error={!!errors.fileLink}>
              <FormLabel>
                <Typography>Link zu Originaldatei {isFileLinkRequired && '*'}</Typography>
              </FormLabel>
              <Input {...register('fileLink', { required: isFileLinkRequired })} />
              {errors.fileLink && <FormHelperText>Link zu Originaldatei muss ausgefüllt werden</FormHelperText>}
            </FormControl>
          </Stack>
          <Divider inset={'context'} />
          <Typography level={'title-lg'}>Notiz</Typography>
          <Textarea {...register('notes')} minRows={2} maxRows={4} />
          {!isEditing && (
            <Controller
              name="isTodo"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Switch
                  checked={value}
                  onChange={onChange}
                  endDecorator={'Handlung erforderlich (ist ToDo)'}
                  sx={{ paddingTop: '1rem', alignSelf: 'flex-start' }}
                />
              )}
            />
          )}
          <FormActionButtons
            onAbortClick={handleOnClose}
            disableSubmit={!isFormDirty}
            isLoading={createXrayTestMutation.isPending || updateXrayTestMutation.isPending}>
            {currentResultId && (
              <DeleteLabOrderButton
                orderId={currentResultId}
                patientId={patientId}
                appointmentId={currentResult?.appointmentId}
                orderType={LabOrderDtoType.XRAY}
              />
            )}
          </FormActionButtons>
        </Stack>
      </form>
    </FormProvider>
  );
}

function getDefaultValues(appointment?: AppointmentDto | null, currentResult?: CreateXrayTestDto): FormValues {
  return {
    samplingDate: getDefaultValueForDateInput(
      currentResult?.samplingDate,
      getAppointmentDate(appointment) ?? undefined,
    ),
    samplingTime: currentResult?.samplingTime
      ? getDefaultValueForLocalTimeInput(currentResult.samplingTime)
      : (getAppointmentTimeFrom(appointment) ?? ''),
    samplingAddress: currentResult?.samplingAddress ?? null,
    samplingContact: currentResult?.samplingContact ?? null,
    rxNumber: currentResult?.rxNumber ?? '',
    finding: currentResult?.finding ?? null,
    findingComment: currentResult?.findingComment ?? '',
    findingDoctor: currentResult?.findingDoctor ?? '',
    followup: currentResult?.followup ?? null,
    fileLink: currentResult?.fileLink ?? '',
    file: null,
    notes: currentResult?.notes ?? '',
    isTodo: currentResult?.isTodo ?? false,
  };
}
